(ns frontend.ui.component.lp-portal.table.pdf.view
  (:require
   [frontend.ui.component.lp-portal.stacked-text-group.pdf.view :as stacked-text]
   [frontend.ui.component.lp-portal.page.pdf.view :as pdf-page]
   [frontend.ui.component.dynamic-table.view :as dynamic-table]
   [frontend.ui.style.theme.core :as theme]
   [clojure.string :as string]
   [frontend.ui.component.lp-portal.text-content.pdf.view :as text-content]
   [frontend.ui.component.dynamic-table.row.style :as row-style]
   [frontend.ui.component.lp-portal.card.pdf.view :as card]
   [frontend.ui.component.lp-portal.page.pdf.view :as page]
   [frontend.ui.component.lp-portal.column.pdf.view :as column]))

(defn- odd-even-color
  [rows]
  (map-indexed
   (fn [i row]
     ^{:key (str "odd-even-color-" i)}
     (if (even? i)
       (map (fn [td] (assoc td :fillColor (theme/color [:palette :background]))) row)
       row))
   rows))

(defn- table-row
  ;; TODO handle variants
  ;; Watch-out, table row height (required for variant) is handled at the table level in pdfMake
  [[row-label & row-values] {:keys [colspan variant]}]
  (let [first-row (merge
                   {:text   row-label
                    :margin [10 5 10 0]}
                   (when colspan
                     {:rowSpan colspan}))]
    (cons first-row
          (map (fn [value]
                 (merge
                  {:text      value
                   :margin    [10 5 10 0]
                   :bold      true
                   :alignment "right"}

                  (when colspan
                    {:rowSpan colspan})))
               row-values))))

(defn table
  [rows-data ui-rows-transformations]
  ;; TODO special treatment for first row: it holds the column headers (→ bold)
  (let [ui-rows-transformations-with-default (concat ui-rows-transformations (repeat {}))]
    {:layout   :noBorders
     :fontSize 4
     :table    {:widths "auto"
                :body
                (odd-even-color
                 (map table-row
                      rows-data
                      ui-rows-transformations-with-default))}}))


(defn formatted-value
  [{:keys [cell-styles cell-data-types row-data]} idx]
  (let [cell-style (nth cell-styles idx nil)
        data-type (nth cell-data-types idx nil)
        cell-data (nth row-data idx nil)
        formatted-value (if (and cell-style
                                 data-type
                                 cell-data)
                          (dynamic-table/formatted-cell-data cell-data
                                                             data-type
                                                             cell-style)
                          (str cell-data))]
    formatted-value))

(defn tearsheet-table
  [{:keys [rows margin-top]}]
  (let [font-size 8
        table-config {:margin [0 margin-top 0 0]
                      :layout {:paddingTop    (constantly 9)
                               :paddingBottom (constantly 4)
                               :paddingRight  (constantly 4)
                               :paddingLeft   (constantly 4)
                               :hLineWidth    (constantly 0)
                               :vLineWidth    (constantly 0)
                               :fillColor     (fn [row-index]
                                                (when (even? row-index)
                                                  (theme/color [:palette :background])))}
                      :table  {:widths ["auto" "*"]
                               :body   (->> rows
                                            (map
                                             (fn [row]
                                               (let [{:keys [label value]} row]
                                                 [{:text     label
                                                   :fontSize font-size}
                                                  {:text      value
                                                   :bold      false
                                                   :noWrap    true
                                                   :margin    [10 0 0 0]
                                                   :fontSize  font-size
                                                   :alignment :right}])))
                                            (vec))}}]
    (if (nil? rows)
      {}
      table-config)))

(defn dynamic-stack
  [data]
  (let [table-body (->>
                    data
                    :data
                    (remove nil?)
                    (map-indexed (fn [idx
                                      {:keys [cell-data-types cell-styles next-empty? row-data
                                              row-data-type row-key row-type]
                                       :as   row}]
                                   (if-not row-key
                                     nil
                                     [[(stacked-text/stack-label row-key)
                                       (stacked-text/stack-value (formatted-value row 0))]])))
                    (remove nil?)
                    (vec))]
    (if (empty? table-body)
      {}
      {:layout :noBorders
       :table  {:body table-body}})))

(defn limit-dynamic-stack
  ([data]
   (limit-dynamic-stack data 14))
  ([data limit]
   (let [dyn-data (:data data)]
     (if (> (count dyn-data) limit)
       (assoc data :data (take limit dyn-data))
       data))))

(defn continue-dynamic-stack
  ([data]
   (continue-dynamic-stack data 14))
  ([data limit]
   (let [dyn-data (:data data)]
     (if (> (count dyn-data) limit)
       (assoc data :data (nthrest dyn-data limit))
       (assoc data :data [])))))

(defn heading-cell
  [{:keys [text align-right] :as params}]
  {:text      (string/upper-case (str text))
   :bold      true
   :alignment (when align-right "right")})

(defn is-total-row?
  [rows content-row-index]
  (-> rows
      (nth content-row-index nil)
      :row-style
      :border-top
      (= :double)))

(defn is-empty-row?
  [rows content-row-index]
  (-> rows
      (nth content-row-index nil)
      (nil?)))

(defn is-subtotal-row?
  [rows content-row-index]
  (-> rows
      (nth content-row-index nil)
      :row-style
      :border-top
      (= :thin)))

(defn show-heading-border?
  [rows index skip-headers?]
  (let [content-row-index (if skip-headers? index (dec index))]
    (and (not skip-headers?) (= 1 index))))

(defn show-top-border?
  [rows index skip-headers?]
  (let [content-row-index (if skip-headers? index (dec index))]
    (or (and (not skip-headers?) (= 1 index))
        (is-total-row? rows content-row-index))))

(defn show-subtotal-border?
  [rows index skip-headers?]
  (let [content-row-index (if skip-headers? index (dec index))]
    (or (and (not skip-headers?) (= 1 index))
        (is-subtotal-row? rows content-row-index))))

(defn is-heading-row?
  [row]
  (-> row
      :row-style
      :underline
      (= :single)))

(defn wrap-text
  ([text threshold]
   (wrap-text text threshold threshold))
  ([text threshold cursor]
   (let [wrap-index (clojure.string/index-of (str text) " " cursor)]
     (if wrap-index
       (let [left (subs text 0 wrap-index)
             right (subs text (inc wrap-index))
             wrapped-text (str left "\n" right)
             new-cursor (+ wrap-index threshold 1)]
         (wrap-text wrapped-text threshold new-cursor))
       text))))

(defn index-of-nearest-heading-row-above
  [rows index]
  (->> rows
       (map-indexed
        (fn [row-index row]
          {:row-index      row-index
           :is-heading-row (is-heading-row? row)}))
       (take (inc index))
       (reverse)
       (filter :is-heading-row)
       (first)
       :row-index))


;; odd-color > transparent by default
;; even-color > transparent by default
;; heading-color > start alternate color schemas from last heading row
(defn dynamic-table
  [{:keys [parsed-data hidden-columns odd-color even-color heading-color skip-title-cell?
           skip-headers? full-width? wrap-text-after partitioned?]
    :as   params}]
  (let [{:keys [ordered-cols col-data-types title data]} parsed-data
        font-size 6
        heading-texts (cons title ordered-cols)
        last-heading-idx (atom 0)
        table-style (cond
                      (->> parsed-data
                           :data
                           (remove nil?)
                           (some row-style/sub-heading?))
                      :table-style/subheadings
                      (->> parsed-data
                           :data
                           (remove nil?)
                           (some row-style/sub-total?))
                      :table-style/subtotals
                      :else
                      :table-style/default)
        hidden-column-indices (->> ordered-cols
                                   (keep-indexed
                                    (fn [index column-heading-text]
                                      (when (contains? hidden-columns column-heading-text)
                                        index)))
                                   (set))
        rows (->> parsed-data
                  :data)
        col-count (or (some->> rows
                               (first)
                               :row-data
                               (count)
                               (inc))
                      0)
        first-col-width (cond (> col-count 11)
                              110
                              (> col-count 9)
                              200
                              (> col-count 5)
                              300)
        page-margins 56
        remaining-width (- pdf-page/page-width first-col-width page-margins)
        auto-size-limit 5
        col-margins 6
        other-col-width (- (float (/ remaining-width (dec col-count)))
                           col-margins)
        table-widths (->> rows
                          (remove nil?)
                          (first)
                          :row-data
                          (map (fn [_]
                                 (cond
                                   full-width?
                                   "auto"
                                   (<= col-count auto-size-limit)
                                   "auto"
                                   (and (not full-width?)
                                        (> col-count 5))
                                   other-col-width
                                   :else
                                   "auto"
                                 )))
                          (cons (cond
                                  full-width?
                                  "*"
                                  (and partitioned? <= col-count auto-size-limit)
                                  300
                                  (<= col-count auto-size-limit)
                                  "auto"
                                  (and (not full-width?)
                                       (> col-count 5))
                                  first-col-width
                                  :else
                                  "*"
                                ))
                          (vec))
        table-config
        {:layout   {:hLineColor (fn [index]
                                  (cond (show-heading-border? rows index skip-headers?)
                                        (theme/color [:pdf :table :header-border])
                                        (show-top-border? rows index skip-headers?)
                                        (theme/color [:pdf :table :border])
                                        (show-subtotal-border? rows index skip-headers?)
                                        (theme/color [:pdf :table :total-border])
                                  ))

                    :hLineWidth (fn [index]
                                  (cond (show-top-border? rows index skip-headers?)
                                        1
                                        (show-subtotal-border? rows index skip-headers?)
                                        1))
                    :vLineWidth (constantly 0)
                    :fillColor  (fn [index]
                                  (cond
                                    (= table-style :table-style/subtotals)
                                    "white"

                                    (= table-style :table-style/subheadings)
                                    (let [content-row-index (if skip-headers? index (dec index))
                                          heading-index (index-of-nearest-heading-row-above
                                                         rows
                                                         content-row-index)
                                          distance-from-nearest-heading-above
                                          (when (some?
                                                 heading-index)
                                            (-
                                             content-row-index
                                             heading-index))
                                          is-total-row (is-total-row? rows content-row-index)
                                          is-empty-row (is-empty-row? rows content-row-index)]
                                      (cond
                                        is-empty-row
                                        even-color

                                        is-total-row
                                        even-color

                                        (nil? distance-from-nearest-heading-above)
                                        (if (odd? content-row-index)
                                          even-color
                                          odd-color)

                                        (= 0 distance-from-nearest-heading-above)
                                        heading-color

                                        :else
                                        (if (even? distance-from-nearest-heading-above)
                                          even-color
                                          odd-color)))
                                    :else
                                    (let [content-row-index (if skip-headers? index (dec index))
                                          is-total-row (is-total-row? rows content-row-index)]
                                      (cond is-total-row
                                            even-color
                                            (even? content-row-index)
                                            even-color
                                            :else
                                            odd-color))))
                   }
         :color    (theme/color [:pdf :table :text])
         :fontSize font-size

         :margin   [0 0 0 0]
         :table    {:headerRows (if skip-headers? 0 1)
                    :widths     table-widths
                    :body       (concat
                                 (when-not skip-headers?
                                   [(concat
                                     [(heading-cell {:text (when-not skip-title-cell? title)
                                                     ;:margin [col-left-margin, 0, col-right-margin, 4]
                                                    })]
                                     (->>
                                      ordered-cols
                                      (map-indexed
                                       (fn [col-index heading-text]
                                         (let [col-data-type (nth col-data-types col-index nil)]
                                           (heading-cell
                                            {:text        (wrap-text heading-text
                                                                     (or wrap-text-after 20))
                                             ;  :margin      [col-left-margin, 0, col-right-margin, 4]
                                             :align-right true}))))
                                      (keep-indexed
                                       (fn [col-index cell-config]
                                         (when (not (contains? hidden-columns
                                                               (nth ordered-cols col-index)))
                                           cell-config)))))])
                                 (->>
                                  rows
                                  (mapv
                                   (fn [row]
                                     (if (nil? row)
                                       (->> (range 0 (count table-widths))
                                            (mapv (fn [i]
                                                    [{:text     " "
                                                      :fontSize 2}])))
                                       (concat
                                        [(merge {:text (-> row
                                                           :row-key
                                                           (string/upper-case)
                                                       )
                                                 ; :margin [col-left-margin, 4, col-right-margin, 4]

                                                 :bold true}
                                                (when (is-heading-row? row)
                                                  {:color (theme/color [:pdf :table :heading :text])}))]
                                        (->> row
                                             :row-data
                                             (map-indexed
                                              (fn [idx cell]
                                                (merge
                                                 {:text      (formatted-value row idx)
                                                  :alignment "right"
                                                  ;   :margin    [col-left-margin, 4, col-right-margin, 4]
                                                  :bold      (when (-> row
                                                                       :cell-styles
                                                                       (vec)
                                                                       (nth idx)
                                                                       :bold?)
                                                               true)}
                                                 (when (is-heading-row? row)
                                                   {:color (theme/color [:pdf :table :heading :text])}))))
                                             (keep-indexed
                                              (fn [col-index cell-config]
                                                (when (not (contains? hidden-columns
                                                                      (nth ordered-cols col-index)))
                                                  cell-config)))
                                             (vec))))))))}}]
    table-config))

(defn raw-table
  [rows]
  (let [table-config
        {:layout   {:hLineColor (fn [index]
                                  (theme/color [:pdf :table :raw-border]))
                    :hLineWidth (fn [index]
                                  (when (= 1 index)
                                    1))
                    :vLineWidth (constantly 0)
                    :fillColor  (fn [row-index]
                                  (if (odd? row-index)
                                    (theme/color [:pdf :table :odd-color])
                                    (theme/color [:pdf :table :even-color])))}
         :fontSize 6
         :table    {:widths (->> rows
                                 (first)
                                 (map-indexed
                                  (fn [cell-index cell]
                                    (if (= cell-index 0)
                                      "*"
                                      "auto"))))
                    :body   (->> rows
                                 (map-indexed
                                  (fn [row-index row]
                                    (let [first-row? (= 0 row-index)]
                                      (->> row
                                           (map-indexed
                                            (fn [cell-index cell]
                                              (let [first-cell? (= 0 cell-index)
                                                    is-heading? (or first-row? first-cell?)]
                                                {:text      (cond
                                                              first-row? (-> cell
                                                                             (or "")
                                                                             (string/upper-case)
                                                                             (wrap-text 14))
                                                              first-cell? (-> cell
                                                                              (or "")
                                                                              (string/upper-case))
                                                              :else cell)
                                                 :bold      is-heading?
                                                 :alignment (if first-cell?
                                                              :left
                                                              :right)})))))))
                                 (vec))}}]
    table-config))

(defn dynamic-table-card
  ([title data]
   (dynamic-table-card title data {} {}))
  ([title data opts]
   (dynamic-table-card title data opts {}))
  ([title data opts footer-opts]
   (card/card
    title
    (dynamic-table (merge {:parsed-data (-> data
                                            :figures)
                           :odd-color   (theme/color [:pdf :fill])}
                          opts))
    (text-content/optional-footer data footer-opts))))

(defn dynamic-table-page
  ([page-title-params title data]
   (dynamic-table-page page-title-params title data {}))
  ([page-title-params title data opts]
   (dynamic-table-page page-title-params title data opts {}))
  ([page-title-params title data opts footer-opts]
   (when (some->> data
                  :figures
                  :data
                  (seq))
     (page/page
      (page/page-title page-title-params)
      (column/column
       (dynamic-table-card title data opts footer-opts))))))

(defn dynamic-table-pages
  ([page-title-params title data]
   (dynamic-table-pages page-title-params title data {}))
  ([page-title-params title data opts]
   (dynamic-table-pages page-title-params title data opts {}))
  ([page-title-params title data opts footer-opts]
   (let [partition-size (get opts :partition-size 25)
         partitions (some->>
                     data
                     :figures
                     :data
                     ;; drop empty lines from end
                     (reverse)
                     (drop-while nil?)
                     (reverse)
                     (partition partition-size partition-size nil)
                     (seq))
         partition-count (count partitions)]
     (some->>
      partitions
      (map-indexed (fn [idx page-batch]
                     (let [batch-data (-> data
                                          (assoc-in [:figures :data] page-batch))
                           batch-data (if (not= idx (dec partition-count))
                                        (dissoc batch-data :rich-content)
                                        batch-data)]
                       (page/page
                        (page/page-title (if (= idx 0)
                                           page-title-params
                                           (dissoc page-title-params :toc-item)))
                        (column/column
                         (dynamic-table-card (if (= 1 partition-count)
                                               title
                                               (str title " (" (inc idx) "/" partition-count ")"))
                                             batch-data
                                             (assoc opts
                                                    :partitioned? (< 1 partition-count)
                                                    :page-title title)
                                             footer-opts))))))))))
